import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  useTheme,
  alpha,
  CircularProgress,
  Chip,
  IconButton,
  Stack,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  People as PeopleIcon,
  HelpOutline as HelpOutlineIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Analytics as AnalyticsIcon,
  CalendarToday as CalendarTodayIcon,
  Update as UpdateIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import useStatistics from "./useStatistics";

export interface StatisticsProps {
  comId: string;
}

/**
 * 통계분석 템플릿 컴포넌트
 * @description 도장의 FAQ(현재 상태), 트라이얼/QNA/GA4 통계(기간별)를 전문적인 UI로 표시합니다.
 * @param comId 도장 ID
 */
export const Statistics = ({ comId }: StatisticsProps) => {
  const theme = useTheme();

  // 비즈니스 로직을 Hook으로 분리
  const { stats, isLoading, error, period, refreshStats, changePeriod } =
    useStatistics({ comId });

  // 현재 년월 정보
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentMonthStr = `${currentYear}년 ${currentMonth
    .toString()
    .padStart(2, "0")}월`;

  // 기간 설정 옵션
  const periodOptions = [
    { value: "7days", label: "7일", description: "최근 1주일" },
    { value: "30days", label: "30일", description: "최근 1개월" },
    { value: "90days", label: "90일", description: "최근 3개월" },
  ] as const;

  // FAQ 통계 카드 설정 (기간과 무관)
  const getFaqCardConfig = () => ({
    title: "FAQ 관리",
    value: stats?.faqStats?.totalCount || 0,
    activeCount: stats?.faqStats?.activeCount || 0,
    completenessRate: stats?.faqStats?.completenessRate || 0,
    icon: <HelpOutlineIcon />,
    color: theme.palette.info.main,
    bgColor: alpha(theme.palette.info.main, 0.08),
    description: "현재 등록된 FAQ 현황 (기간과 무관)",
  });

  // 기간별 통계 카드들 설정
  const getPeriodStatsConfig = () => [
    {
      title: "트라이얼 신청",
      value: stats?.trialStats?.totalCount || 0,
      thisMonth: stats?.trialStats?.thisMonthCount || 0,
      growth: stats?.trialStats?.growthRate || 0,
      conversionRate: stats?.trialStats?.conversionRate || 0,
      pendingCount: stats?.trialStats?.pendingCount || 0,
      icon: <PeopleIcon />,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.08),
      description: "체험 수업 신청 현황",
    },
    {
      title: "고객 문의",
      value: stats?.qnaStats?.totalCount || 0,
      thisMonth: stats?.qnaStats?.thisMonthCount || 0,
      growth: stats?.qnaStats?.growthRate || 0,
      responseRate: stats?.qnaStats?.responseRate || 0,
      pendingCount: stats?.qnaStats?.pendingCount || 0,
      icon: <QuestionAnswerIcon />,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.08),
      description: "문의사항 접수 현황",
    },
  ];

  // 에러 상태
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        gap={2}
      >
        <Typography variant="h6" color="error">
          데이터 로딩 중 오류가 발생했습니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <IconButton onClick={refreshStats} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, width: "100%", maxWidth: "none" }}>
      {/* 헤더 */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight="700"
            color="text.primary"
            gutterBottom
          >
            도장 통계 대시보드
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography variant="body1" color="text.secondary">
              홈페이지 트래픽과 문의/트라이얼 연관성 분석
            </Typography>
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
              label={`현재: ${currentMonthStr}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          {stats?.lastUpdated && (
            <Box display="flex" alignItems="center" gap={1}>
              <UpdateIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                마지막 업데이트:{" "}
                {new Date(stats.lastUpdated).toLocaleString("ko-KR")}
              </Typography>
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            기간별 분석:
          </Typography>
          {/* 기간 선택 */}
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, newPeriod) => {
              if (newPeriod) changePeriod(newPeriod);
            }}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.5,
                fontSize: "0.875rem",
              },
            }}
          >
            {periodOptions.map((option) => (
              <ToggleButton
                key={option.value}
                value={option.value}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* 새로고침 버튼 */}
          <IconButton
            onClick={refreshStats}
            disabled={isLoading}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
              "&:disabled": {
                backgroundColor: alpha(theme.palette.grey[400], 0.08),
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* FAQ 현황 카드 (기간과 무관) */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: getFaqCardConfig().bgColor,
                  color: getFaqCardConfig().color,
                  mr: 2,
                }}
              >
                {getFaqCardConfig().icon}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600" color="text.primary">
                  {getFaqCardConfig().title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  현재 등록된 FAQ 현황 (기간과 무관)
                </Typography>
              </Box>
            </Box>

            <Box textAlign="right">
              <Typography variant="h3" fontWeight="700" color="success.main">
                {getFaqCardConfig().activeCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                활성화된 FAQ
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 기간별 통계 카드들 */}
      <Typography
        variant="h6"
        fontWeight="600"
        color="text.primary"
        sx={{ mb: 2 }}
      >
        📊 기간별 통계 (
        {periodOptions.find((p) => p.value === period)?.description})
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {getPeriodStatsConfig().map((stat, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={index}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                borderRadius: 3,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: stat.color,
                  boxShadow: `0 4px 20px ${alpha(stat.color, 0.15)}`,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* 카드 헤더 */}
                <Box display="flex" alignItems="center" mb={2.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: stat.bgColor,
                      color: stat.color,
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color="text.primary"
                    >
                      {stat.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.description}
                    </Typography>
                  </Box>
                </Box>

                {/* 메인 숫자 */}
                <Box mb={2}>
                  {/* 기간별 데이터 (상단) */}
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        label={
                          periodOptions.find((p) => p.value === period)
                            ?.description || "기간별"
                        }
                        size="small"
                        color="primary"
                        sx={{
                          fontSize: "0.7rem",
                          height: 20,
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      fontWeight="700"
                      color="primary.main"
                      sx={{ lineHeight: 1.2 }}
                    >
                      {stat.thisMonth.toLocaleString()}건
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      선택된 기간의 현재 월 데이터
                    </Typography>
                  </Box>

                  {/* 전체 누적 데이터 (하단) */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        label="전체 누적"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{
                          fontSize: "0.7rem",
                          height: 20,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                          borderColor: alpha(theme.palette.grey[400], 0.5),
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight="600"
                      color="text.secondary"
                      sx={{ lineHeight: 1.2 }}
                    >
                      총 {stat.value.toLocaleString()}건
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      전체 기간 누적 데이터 (기간 선택과 무관)
                    </Typography>
                  </Box>
                </Box>

                {/* 상세 지표들 */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {stat.title === "트라이얼 신청" && (
                    <>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          전환율:
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {Math.round(stat.conversionRate || 0)}%
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          대기 중:
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {stat.pendingCount}건
                        </Typography>
                      </Box>
                    </>
                  )}

                  {stat.title === "고객 문의" && (
                    <>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          답변률:
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {Math.round(stat.responseRate || 0)}%
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          답변 대기:
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {stat.pendingCount}건
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>

                {/* 성장률 */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center">
                    {stat.growth >= 0 ? (
                      <TrendingUpIcon
                        sx={{
                          color: theme.palette.success.main,
                          fontSize: 20,
                          mr: 0.5,
                        }}
                      />
                    ) : (
                      <TrendingDownIcon
                        sx={{
                          color: theme.palette.error.main,
                          fontSize: 20,
                          mr: 0.5,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color={stat.growth >= 0 ? "success.main" : "error.main"}
                    >
                      {stat.growth >= 0 ? "+" : ""}
                      {Math.round(stat.growth)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    vs 전월
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* GA4 트래픽 섹션 */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          borderRadius: 3,
          mb: 4,
          width: "100%",
          maxWidth: "none",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* 섹션 헤더 */}
          <Box display="flex" alignItems="center" mb={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                color: theme.palette.info.main,
                mr: 3,
              }}
            >
              <AnalyticsIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" fontWeight="700" color="text.primary">
                웹사이트 트래픽 (
                {periodOptions.find((p) => p.value === period)?.description})
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                mt={1}
                flexWrap="wrap"
                gap={1}
              >
                <Chip
                  label={
                    stats?.ga4Traffic?.hasConnection
                      ? "Google Analytics 연결됨"
                      : "Google Analytics 미연결"
                  }
                  size="small"
                  color={
                    stats?.ga4Traffic?.hasConnection ? "success" : "default"
                  }
                />
                <Chip
                  label="기간별 필터링됨"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{
                    fontSize: "0.7rem",
                    height: 20,
                  }}
                />
                {stats?.ga4Traffic?.hasConnection && (
                  <>
                    <Chip
                      label={
                        periodOptions.find((p) => p.value === period)
                          ?.description || period
                      }
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                    <Typography variant="caption" color="text.secondary">
                      실시간 데이터 동기화 중
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {stats?.ga4Traffic?.hasConnection ? (
            <>
              {/* 주요 지표 */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="primary.main"
                      sx={{ mb: 0.5 }}
                    >
                      {stats.ga4Traffic.totalSessions.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      총 세션
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="primary.main"
                      sx={{ mb: 0.5 }}
                    >
                      {stats.ga4Traffic.totalUsers.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      총 사용자
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="primary.main"
                      sx={{ mb: 0.5 }}
                    >
                      {Math.round(stats.ga4Traffic.avgSessionDuration)}s
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      평균 세션시간
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* 일별 세션 추이 차트 - 전체 너비 */}
              <Grid container spacing={3} sx={{ mb: 4, width: "100%" }}>
                <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(theme.palette.grey[50], 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.grey[300],
                        0.5
                      )}`,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      mb={2}
                      color="text.primary"
                    >
                      📊 일별 세션 추이
                    </Typography>
                    <Box sx={{ width: "100%", height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={(() => {
                            // 백엔드 데이터를 날짜 순으로 정렬
                            const sortedDailyStats = stats.ga4Traffic.dailyStats
                              ? [...stats.ga4Traffic.dailyStats].sort(
                                  (a, b) =>
                                    new Date(a.date).getTime() -
                                    new Date(b.date).getTime()
                                )
                              : [];

                            const dailyData = sortedDailyStats.map((day) => ({
                              date: new Date(day.date).toLocaleDateString(
                                "ko-KR",
                                {
                                  month: "short",
                                  day: "numeric",
                                }
                              ),
                              sessions: day.sessions,
                              users: day.users,
                            }));

                            // 데이터가 없을 경우 더미 데이터 제공 (시간 순서대로)
                            if (dailyData.length === 0) {
                              const dummyData = [];
                              const today = new Date();
                              for (let i = 6; i >= 0; i--) {
                                const date = new Date(today);
                                date.setDate(today.getDate() - i);
                                dummyData.push({
                                  date: date.toLocaleDateString("ko-KR", {
                                    month: "short",
                                    day: "numeric",
                                  }),
                                  sessions: Math.floor(Math.random() * 50) + 10,
                                  users: Math.floor(Math.random() * 40) + 8,
                                });
                              }
                              return dummyData;
                            }

                            console.log("[CHART_DATA]", {
                              originalData: stats.ga4Traffic.dailyStats,
                              sortedData: dailyData,
                            });
                            return dailyData;
                          })()}
                          margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={alpha(theme.palette.grey[400], 0.3)}
                          />
                          <XAxis
                            dataKey="date"
                            fontSize={12}
                            stroke={theme.palette.text.secondary}
                          />
                          <YAxis
                            fontSize={12}
                            stroke={theme.palette.text.secondary}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}`,
                              borderRadius: 8,
                              boxShadow: theme.shadows[3],
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="sessions"
                            stroke={theme.palette.primary.main}
                            strokeWidth={3}
                            dot={{
                              fill: theme.palette.primary.main,
                              strokeWidth: 2,
                              r: 4,
                            }}
                            activeDot={{
                              r: 6,
                              stroke: theme.palette.primary.main,
                              strokeWidth: 2,
                            }}
                            name="세션 수"
                          />
                          <Line
                            type="monotone"
                            dataKey="users"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{
                              fill: theme.palette.success.main,
                              strokeWidth: 2,
                              r: 3,
                            }}
                            name="사용자 수"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* 사용자 구성과 트래픽 소스 차트 */}
              <Grid container spacing={3} sx={{ mb: 4, width: "100%" }}>
                {/* 사용자 구성 도넛 차트 */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(theme.palette.grey[50], 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.grey[300],
                        0.5
                      )}`,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      mb={2}
                      color="text.primary"
                    >
                      👥 사용자 구성
                    </Typography>
                    <Box sx={{ width: "100%", height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: "신규 사용자",
                                value: stats.ga4Traffic.newUsers,
                                color: theme.palette.primary.main,
                              },
                              {
                                name: "재방문 사용자",
                                value: stats.ga4Traffic.returningUsers,
                                color: theme.palette.success.main,
                              },
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={120}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill={theme.palette.primary.main} />
                            <Cell fill={theme.palette.success.main} />
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => [
                              value.toLocaleString(),
                              "",
                            ]}
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}`,
                              borderRadius: 8,
                              boxShadow: theme.shadows[3],
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            wrapperStyle={{
                              fontSize: "12px",
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Grid>

                {/* 트래픽 소스 파이 차트 */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(theme.palette.grey[50], 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.grey[300],
                        0.5
                      )}`,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      mb={2}
                      color="text.primary"
                    >
                      🌐 트래픽 소스 분석
                    </Typography>
                    <Box sx={{ width: "100%", height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={
                              stats.ga4Traffic.topTrafficSources
                                ?.slice(0, 6)
                                .map((source, index) => ({
                                  name: source.source,
                                  value: source.sessions,
                                  color: [
                                    theme.palette.primary.main,
                                    theme.palette.success.main,
                                    theme.palette.warning.main,
                                    theme.palette.error.main,
                                    theme.palette.info.main,
                                    theme.palette.purple?.[500] ||
                                      theme.palette.secondary.main,
                                  ][index],
                                })) || []
                            }
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                            labelLine={false}
                          >
                            {stats.ga4Traffic.topTrafficSources
                              ?.slice(0, 6)
                              .map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    [
                                      theme.palette.primary.main,
                                      theme.palette.success.main,
                                      theme.palette.warning.main,
                                      theme.palette.error.main,
                                      theme.palette.info.main,
                                      theme.palette.purple?.[500] ||
                                        theme.palette.secondary.main,
                                    ][index]
                                  }
                                />
                              ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => [
                              value.toLocaleString() + " 세션",
                              "",
                            ]}
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}`,
                              borderRadius: 8,
                              boxShadow: theme.shadows[3],
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            wrapperStyle={{
                              fontSize: "12px",
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* 성장률 & 요약 */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: alpha(theme.palette.grey[50], 0.5),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  mb={2}
                  color="text.primary"
                >
                  📈 성장률 & 요약
                </Typography>

                <Grid container spacing={3}>
                  {/* 성장률 */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: alpha(
                          stats.ga4Traffic.growthRate >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.1
                        ),
                        borderRadius: 2,
                        border: `1px solid ${alpha(
                          stats.ga4Traffic.growthRate >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        mb={1}
                      >
                        {stats.ga4Traffic.growthRate >= 0 ? (
                          <TrendingUpIcon
                            sx={{
                              color: theme.palette.success.main,
                              fontSize: 24,
                            }}
                          />
                        ) : (
                          <TrendingDownIcon
                            sx={{
                              color: theme.palette.error.main,
                              fontSize: 24,
                            }}
                          />
                        )}
                        <Typography
                          variant="h4"
                          fontWeight="700"
                          color={
                            stats.ga4Traffic.growthRate >= 0
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {stats.ga4Traffic.growthRate >= 0 ? "+" : ""}
                          {Math.round(stats.ga4Traffic.growthRate)}%
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        전월 대비 성장률
                      </Typography>
                    </Box>
                  </Grid>

                  {/* 주요 통계 요약 */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        상위 트래픽 소스
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="primary.main"
                      >
                        {stats.ga4Traffic.topTrafficSources?.[0]?.source ||
                          "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.ga4Traffic.topTrafficSources?.[0]?.sessions.toLocaleString() ||
                          0}{" "}
                        세션
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        신규 사용자 비율
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="success.main"
                      >
                        {Math.round(
                          (stats.ga4Traffic.newUsers /
                            stats.ga4Traffic.totalUsers) *
                            100
                        )}
                        %
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        전체 사용자 중{" "}
                        {stats.ga4Traffic.newUsers.toLocaleString()}명
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                backgroundColor: alpha(theme.palette.grey[100], 0.3),
                borderRadius: 2,
              }}
            >
              <AnalyticsIcon
                sx={{
                  fontSize: 64,
                  color: theme.palette.grey[400],
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary" mb={1}>
                Google Analytics 4 연결이 필요합니다
              </Typography>
              <Typography variant="body2" color="text.secondary">
                도장 관리 페이지에서 GA4를 연결하시면
                <br />
                상세한 웹사이트 트래픽 분석을 확인할 수 있습니다
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 요약 정보 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.info.main, 0.04),
          border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
          borderRadius: 3,
        }}
      >
        <Typography variant="body1" fontWeight="600" color="info.main" mb={1}>
          📊 데이터 기준 및 설정
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon sx={{ fontSize: 16, color: "info.main" }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>현재 기준월:</strong> {currentMonthStr}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <AnalyticsIcon sx={{ fontSize: 16, color: "info.main" }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>조회 기간:</strong>{" "}
                  {periodOptions.find((p) => p.value === period)?.description}
                </Typography>
              </Box>
              {stats?.startDate && stats?.endDate && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>데이터 범위:</strong>{" "}
                    {new Date(stats.startDate).toLocaleDateString("ko-KR")} ~{" "}
                    {new Date(stats.endDate).toLocaleDateString("ko-KR")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                • FAQ는 현재 등록된 상태만 표시 (기간과 무관)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>트라이얼/문의 총건수:</strong> 전체 기간 누적 데이터
                (기간 선택과 무관)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>트라이얼/문의 이번달:</strong> 현재 월 기준 데이터
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>웹사이트 트래픽:</strong> 선택한 기간(
                {periodOptions.find((p) => p.value === period)?.description})에
                맞춰 필터링됨
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 전월 대비 성장률은 직전월과의 비교 수치입니다
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 모든 데이터는 실시간으로 업데이트됩니다
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
